// Training Center — top-level page for /training. Six tabs that
// mirror web/training.html: Dashboard (quality flywheel), Evals,
// Recommendations, Prompt Examples, Semantic Model, Golden Datasets.
//
// Routing: ?tab=… search param; pushState on tab change so admins can
// deep-link a specific tab (e.g. paste the Semantic Model URL into a
// review thread).

import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../api/admin";
import { getSession } from "../../auth/session";
import { DashboardTab } from "./DashboardTab";
import { EvalsTab } from "./EvalsTab";
import { GoldenDatasetsTab } from "./GoldenDatasetsTab";
import { PromptExamplesTab } from "./PromptExamplesTab";
import { RecommendationsTab } from "./RecommendationsTab";
import { SemanticModelTab } from "./SemanticModelTab";

export type TrainingTab =
  | "dashboard"
  | "evals"
  | "recommendations"
  | "examples"
  | "semantic-model"
  | "golden-eval";

const TABS: { id: TrainingTab; label: string }[] = [
  { id: "dashboard", label: "Dashboard" },
  { id: "evals", label: "Evals" },
  { id: "recommendations", label: "Recommendations" },
  { id: "examples", label: "Prompt Examples" },
  { id: "semantic-model", label: "Semantic Model" },
  { id: "golden-eval", label: "Golden Datasets" },
];

interface Props {
  bearer: string;
}

export function TrainingPage({ bearer }: Props) {
  const session = getSession();
  const role = session?.role ?? "Admin";
  const isSuperAdmin = role === "Super Admin";
  const isAdmin = role === "Admin";
  const allowed = isSuperAdmin || isAdmin;

  const ownOrg = session?.defaultOrganization ?? "";
  const [organization, setOrganization] = useState<string>(isSuperAdmin ? "" : ownOrg);
  const [orgList, setOrgList] = useState<string[]>([]);

  const [tab, setTab] = useState<TrainingTab>(() => readTabFromUrl());
  useEffect(() => {
    const onChange = () => setTab(readTabFromUrl());
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);
  const selectTab = (next: TrainingTab) => {
    if (next === tab) return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", next);
    window.history.pushState(null, "", url.toString());
    setTab(next);
  };

  // Super Admin org dropdown — reuses /admin/organizations.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    adminApi
      .organizations(bearer, true)
      .then((orgs) => {
        if (!cancelled) setOrgList(orgs);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [bearer, isSuperAdmin]);

  const scope = useMemo(() => ({ organization }), [organization]);

  if (!allowed) {
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center">
        <h1 className="text-xl font-semibold text-neutral-900">Access denied</h1>
        <p className="mt-3 text-sm text-neutral-600">
          The Training Center is restricted to Admin and Super Admin users.
        </p>
        <a
          href="/"
          className="mt-6 inline-block rounded-full bg-brand text-white text-sm px-4 h-9 leading-9 hover:bg-brand-600"
        >
          Back to Search
        </a>
      </div>
    );
  }

  return (
    <div className="px-6 pt-6 pb-12">
      <div className="flex items-end justify-between gap-4 border-b border-line pb-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Training Center</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Quality flywheel, evaluations, recommendations, examples, semantic model, golden datasets.
          </p>
        </div>
        {isSuperAdmin && (
          <OrgFilter value={organization} onChange={setOrganization} options={orgList} />
        )}
      </div>

      <nav className="mt-4 flex gap-1 border-b border-line overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(t.id)}
            className={
              "px-3 h-9 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap " +
              (tab === t.id
                ? "text-neutral-900 border-brand"
                : "text-neutral-500 border-transparent hover:text-neutral-800")
            }
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === "dashboard" && <DashboardTab bearer={bearer} scope={scope} />}
        {tab === "evals" && <EvalsTab bearer={bearer} scope={scope} />}
        {tab === "recommendations" && (
          <RecommendationsTab bearer={bearer} scope={scope} />
        )}
        {tab === "examples" && <PromptExamplesTab bearer={bearer} scope={scope} />}
        {tab === "semantic-model" && (
          <SemanticModelTab bearer={bearer} scope={scope} isSuperAdmin={isSuperAdmin} />
        )}
        {tab === "golden-eval" && <GoldenDatasetsTab bearer={bearer} scope={scope} />}
      </div>
    </div>
  );
}

function readTabFromUrl(): TrainingTab {
  const raw = new URL(window.location.href).searchParams.get("tab");
  const ids: TrainingTab[] = [
    "dashboard",
    "evals",
    "recommendations",
    "examples",
    "semantic-model",
    "golden-eval",
  ];
  return (ids as string[]).includes(raw ?? "") ? (raw as TrainingTab) : "dashboard";
}

interface OrgFilterProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}

function OrgFilter({ value, onChange, options }: OrgFilterProps) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-neutral-500" htmlFor="training-org-filter">
        Organization
      </label>
      <select
        id="training-org-filter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 px-2 pr-7 text-sm rounded-md border border-line bg-white text-neutral-800 hover:bg-chip focus:outline-none focus:ring-1 focus:ring-brand"
      >
        <option value="">All orgs</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
